import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { useMfa } from "@/hooks/use-mfa";
import { useAppStore } from "@/store";
import { PageHeader } from "@/components/ui";
import { MfaChooseMethod } from "@/components/mfa-choose-method";
import { MfaTotpScanStep } from "@/components/mfa-totp-scan-step";
import { MfaRecoveryCodesDisplay } from "@/components/mfa-recovery-codes-display";
import { MfaStatusCard } from "@/components/mfa-status-card";
import { ErrorBoundary } from "@/components/error-boundary";

export const Route = createFileRoute("/_authenticated/settings/mfa")({
  // ErrorBoundary wraps MFA settings to catch QR generation or unexpected render errors
  component: () => (
    <ErrorBoundary>
      <MfaSettingsPage />
    </ErrorBoundary>
  ),
});

type SetupStep = "choose" | "totp-scan" | "totp-verify" | "email-setup" | "recovery-codes";

function MfaSettingsPage() {
  const { t } = useTranslation("mfa");
  const { status, setupTotp, verifyTotp, setupEmail, disableMfa, regenerateCodes } = useMfa();
  const emailVerified = useAppStore((s) => s.user?.emailVerified ?? false);
  const [setupStep, setSetupStep] = useState<SetupStep>("choose");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [totpSecret, setTotpSecret] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [codes, setCodes] = useState<string[]>([]);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showDisable, setShowDisable] = useState(false);
  const [showRegen, setShowRegen] = useState(false);
  const onErr = (err: Error) => setError(err.message);
  if (!emailVerified) return null;

  const startTotp = () => {
    setError(null);
    setupTotp.mutate(undefined, {
      onSuccess: async (d) => {
        setTotpSecret(d?.secret ?? "");
        try {
          setQrDataUrl(await QRCode.toDataURL(d?.provisioning_uri ?? "", { width: 256 }));
        } catch {
          setQrDataUrl("");
        }
        setSetupStep("totp-scan");
      },
      onError: onErr,
    });
  };
  const verifyCode = () => {
    setError(null);
    verifyTotp.mutate(
      { code: totpCode },
      {
        onSuccess: (d) => {
          setCodes(d?.recovery_codes ?? []);
          setSetupStep("recovery-codes");
        },
        onError: onErr,
      },
    );
  };
  const enableEmail = () => {
    setError(null);
    setupEmail.mutate(undefined, { onSuccess: () => setSetupStep("choose"), onError: onErr });
  };
  const confirmDisable = () => {
    setError(null);
    disableMfa.mutate(
      { password },
      {
        onSuccess: () => {
          setShowDisable(false);
          setPassword("");
        },
        onError: (err: Error) => {
          onErr(err);
          // Clear password on error to prevent re-submission of wrong password
          setPassword("");
        },
      },
    );
  };
  const confirmRegen = () => {
    setError(null);
    regenerateCodes.mutate(
      { password },
      {
        onSuccess: (d) => {
          setCodes(d?.recovery_codes ?? []);
          setShowRegen(false);
          setPassword("");
          setSetupStep("recovery-codes");
        },
        onError: (err: Error) => {
          onErr(err);
          // Clear password on error to prevent re-submission of wrong password
          setPassword("");
        },
      },
    );
  };
  const copyCodes = () => {
    void navigator.clipboard.writeText(codes.join("\n"));
  };
  const downloadCodes = () => {
    const url = URL.createObjectURL(new Blob([codes.join("\n")], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "njbsoft-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (status.isLoading) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <div className="h-8 w-48 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
      </div>
    );
  }
  if (status.data?.methods && status.data.methods.length > 0 && setupStep === "choose") {
    return (
      <MfaStatusCard
        methods={status.data.methods}
        codesRemaining={status.data.recovery_codes_remaining ?? 0}
        onDisable={() => {
          setShowDisable(true);
          setError(null);
        }}
        onRegenerate={() => {
          setShowRegen(true);
          setError(null);
        }}
        showDisableConfirm={showDisable}
        showRegenConfirm={showRegen}
        password={password}
        onPasswordChange={setPassword}
        disablePending={disableMfa.isPending}
        regenPending={regenerateCodes.isPending}
        onConfirmDisable={confirmDisable}
        onConfirmRegen={confirmRegen}
        onCancelDisable={() => {
          setShowDisable(false);
          setPassword("");
        }}
        onCancelRegen={() => {
          setShowRegen(false);
          setPassword("");
        }}
        error={error}
      />
    );
  }
  if (setupStep === "recovery-codes") {
    return (
      <MfaRecoveryCodesDisplay
        recoveryCodes={codes}
        onCopy={copyCodes}
        onDownload={downloadCodes}
        onDone={() => setSetupStep("choose")}
      />
    );
  }
  if (setupStep === "totp-scan") {
    return (
      <MfaTotpScanStep
        qrDataUrl={qrDataUrl}
        totpSecret={totpSecret}
        totpCode={totpCode}
        onCodeChange={setTotpCode}
        onVerify={verifyCode}
        onCancel={() => setSetupStep("choose")}
        isPending={verifyTotp.isPending}
        error={error}
      />
    );
  }
  return (
    <div className="mx-auto max-w-2xl p-8">
      <PageHeader title={t("setup.title")} />
      <p className="mt-2 text-sm text-muted">{t("setup.description")}</p>
      <MfaChooseMethod
        onChooseTotp={startTotp}
        onChooseEmail={enableEmail}
        isPending={setupTotp.isPending || setupEmail.isPending}
      />
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
    </div>
  );
}
